<template>
  <AuthGuard>
    <v-container class="w-full month-picker-container mb-6" v-if="!preview">
      <PageHeader title="Report Setup" :showBack="true" back-to="/customers">
        <v-tooltip :text="generateReportTooltip" location="bottom">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              @click="generateReport"
              color="primary"
              class="w-100 w-sm-auto"
              :loading="state.isGeneratePDFLoading"
              :disabled="
                !hasReportBuildPermission ||
                !isLoadingFinished ||
                !everyCustomerHasAtLeastOneSource ||
                state.isSummaryLoading ||
                state.isSummarySaveLoading
              "
              text="Generate Report"
            />
          </template>
        </v-tooltip>
      </PageHeader>

      <v-alert v-if="!hasReportBuildPermission" type="error" variant="tonal" class="mb-6">
        {{ reportBuildPermissionMessage }}
      </v-alert>

      <Section v-if="hasReportBuildPermission">
        <div
          class="d-flex flex-column flex-sm-row justify-start justify-md-space-between align-start align-sm-center mb-4 mb-sm-0"
        >
          <div class="d-flex flex-column">
            <span class="font-weight-bold">Select Months to Compare</span>
            <v-btn-toggle v-model="state.numberOfMonths" class="mb-3 btn-range-selector" mandatory>
              <v-btn
                v-for="i in [3, 6, 12]"
                :key="i"
                :value="i"
                :class="{ selected: state.numberOfMonths === i }"
                class="text-transform-none"
              >
                {{ i }} Months
              </v-btn>
            </v-btn-toggle>
          </div>

          <div v-if="tempMonths.selectedMonths.length > 0 && tempMonths.comparedMonths.length > 0">
            <v-btn
              :disabled="!hasMonthRangeChange || !isMonthsSizeEqual"
              class="w-100 w-sm-auto color-primary"
              @click="handleClickApply"
              style="text-transform: none"
              text="Update Date Range"
            />
          </div>
        </div>

        <div>
          <div class="month-picker">
            <div :style="{ color: Colors.Green }">
              <span class="d-inline-block">Current Period</span>
              <span class="text-no-wrap"
                >({{ moment(_.head(tempMonths.selectedMonths), "YYYY-MM").format("MMM YYYY") }} -
                {{ moment(_.last(tempMonths.selectedMonths), "YYYY-MM").format("MMM YYYY") }})</span
              >
            </div>
            <MonthRangePicker
              :numberOfMonths="state.numberOfMonths"
              v-model="tempMonths.selectedMonths"
              :activeColor="Colors.Green"
              @on-scroll-change="state.comparedScrollPosition = $event"
            />
          </div>
          <div class="month-picker mt-4">
            <div :style="{ color: Colors.Blue }">
              <span class="d-inline-block">Previous Period</span>
              <span class="text-no-wrap"
                >({{ moment(_.head(tempMonths.comparedMonths), "YYYY-MM").format("MMM YYYY") }} -
                {{ moment(_.last(tempMonths.comparedMonths), "YYYY-MM").format("MMM YYYY") }})</span
              >
            </div>
            <MonthRangePicker
              :numberOfMonths="state.numberOfMonths"
              v-model="tempMonths.comparedMonths"
              :disabled-months="[
                ...tempMonths.selectedMonths,
                ...Array.from(
                  iterateMonths(
                    moment(_.last(tempMonths.selectedMonths), 'YYYY-MM')
                      .add(1, 'months')
                      .format('YYYY-MM'),
                    moment(_.last(tempMonths.selectedMonths), 'YYYY-MM')
                      .add(state.numberOfMonths, 'months')
                      .format('YYYY-MM')
                  )
                )
              ]"
              :scroll-position="state.comparedScrollPosition"
            />
          </div>
        </div>
      </Section>
    </v-container>

    <Loading v-if="state.fetchAccountListLoading" message="Loading sources..." />

    <v-container
      v-else-if="hasReportBuildPermission && state.isAccountListFetched"
      :style="{ background: '#f2f2f2', margin: 'auto', width: '850px' }"
    >
      <div
        v-for="(customer, indexCustomer) in state.customers"
        class="print-body print position-relative mt-6 pt-6"
        :key="customer.id"
        :class="{ 'page-break': indexCustomer > 0 && state.summary !== '' }"
      >
        <v-btn
          icon
          class="tw:absolute! tw:right-1! tw:top-1!"
          variant="plain"
          @click="handleClickClose"
          size="40"
          v-if="mode === IApp.PrintMode.Edit && preview"
        >
          <v-icon class="tw:text-3xl!">mdi-close-circle-outline</v-icon>
        </v-btn>

        <div class="tw:hidden!" :data-loading="state.isFetchingPrompt"></div>
        <div class="tw:hidden!" :data-loading="state.isSummarySaveLoading"></div>

        <Section class="mt-2">
          <v-row>
            <v-col align="left">
              <img src="/logo.png" class="logo print-only" alt="logo" />
            </v-col>
            <v-col align="right">
              <h2 class="title title-sm print-only">
                {{ moment(_.last(months.selectedMonths)!, "YYYY-MM").format("MMMM YYYY") }}
              </h2>
            </v-col>
          </v-row>
        </Section>

        <template v-if="!customerHasAnyDataSource(customer)">
          <v-container class="w-full">
            <v-row>
              <v-col cols="12" class="text-center">
                <h3 class="customer-name font-weight-bold text-h6 mt-4">
                  {{ customer?.name }}
                </h3>
                <v-alert type="info" variant="tonal" class="mt-4 text-left">
                  This customer has no data sources connected. Add a data source in customer
                  settings to view the dashboard and generate a report.
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
        </template>

        <template v-else>
          <Section v-if="!isLoadingFinished">
            <v-container class="w-full progress-bar-container">
              <v-progress-linear color="teal" indeterminate />

              <div>
                <span>
                  Getting data for
                  {{ loadingNames.join(", ") }}
                  charts...
                </span>
              </div>
            </v-container>
          </Section>

          <v-row>
            <v-col cols="12" class="text-center">
              <h2 class="title">Digital Visibility Report</h2>
              <div class="mt-2">
                <h3>{{ state.numberOfMonths }}-month Analytics Report</h3>
                <span>
                  {{ moment(_.head(months.selectedMonths), "YYYY-MM").format("MMM YYYY") }} -
                  {{ moment(_.last(months.selectedMonths), "YYYY-MM").format("MMM YYYY") }}
                </span>
                <span> vs </span>
                <span>
                  {{ moment(_.head(months.comparedMonths), "YYYY-MM").format("MMM YYYY") }} -
                  {{ moment(_.last(months.comparedMonths), "YYYY-MM").format("MMM YYYY") }}
                </span>
              </div>
              <v-divider class="my-6 mx-auto" :style="{ color: Colors.Blue, width: '200px' }" />
              <h3 class="customer-name font-weight-bold text-h6">
                {{ customer?.name }}
              </h3>
            </v-col>
          </v-row>

          <Section>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <AISummaryEditor
                    :prompt="JSON.stringify(state.totalData)"
                    :mode="hasReportConfirmPermission ? mode : IApp.PrintMode.View"
                    :loading="state.isSummarySaveLoading"
                    :save="onSummarySave"
                    :regenerate="onSummaryRegenerate"
                    :summary="state.summary"
                    :disabled="!isLoadingFinished"
                    :prompt-id="state.report?.promptId"
                  />
                  <div class="page-break" />
                </v-col>
              </v-row>
            </v-container>
          </Section>

          <v-container
            class="w-full"
            v-if="
              !_(customer.accounts)
                .map((item) => !!item.isSynced)
                .some()
            "
          >
            <v-row>
              <v-col align="center">
                <v-alert type="warning" class="mt-2">
                  <span>This customer is not synced yet.</span>
                </v-alert>
              </v-col>
            </v-row>
          </v-container>
          <Section
            v-for="(account, index) in _(filterAccounts(customer.accounts ?? []))
              .filter((item) => !!item.isSynced)
              .value()"
            :color="index % 2 && !preview ? `transparent` : ''"
            :key="account.account"
            :class="index > 0 && !preview ? 'page-break' : ''"
          >
            <v-container class="w-full">
              <v-row>
                <v-col cols="12">
                  <BirdEyeDashboard
                    v-if="account?.feed === IApp.IFeedTypes.BirdEye && account.isSynced"
                    :account="account"
                    :monthRange="{
                      start: _.head(months.selectedMonths)!,
                      end: _.last(months.selectedMonths)!
                    }"
                    :comparedMonthRange="{
                      start: _.head(months.comparedMonths)!,
                      end: _.last(months.comparedMonths)!
                    }"
                    :print-mode="mode"
                    :request-id="requestId"
                    :report-id="reportId"
                    :report="state.report"
                    :mode="mode"
                    :show-summary="showSummary"
                    @on-change-data="
                      upsertTotalData(account.account, 'Business Directory Metrics', $event)
                    "
                    @on-change-data-load="
                      () => (loadingFinishedMap[IApp.IFeedTypes.BirdEye].value = true)
                    "
                  />
                  <GoogleSearchConsoleDashboard
                    v-else-if="
                      account?.feed === IApp.IFeedTypes.GoogleSearchConsole && account.isSynced
                    "
                    :account="account"
                    :monthRange="{
                      start: _.head(months.selectedMonths)!,
                      end: _.last(months.selectedMonths)!
                    }"
                    :comparedMonthRange="{
                      start: _.head(months.comparedMonths)!,
                      end: _.last(months.comparedMonths)!
                    }"
                    :print-mode="mode"
                    :request-id="requestId"
                    :report-id="reportId"
                    :report="state.report"
                    :mode="mode"
                    :show-summary="showSummary"
                    @on-change-data="
                      upsertTotalData(account.account, 'Google Search Metrics', $event)
                    "
                    @on-change-data-load="
                      () => (loadingFinishedMap[IApp.IFeedTypes.GoogleSearchConsole].value = true)
                    "
                  />
                  <GoogleAnalyticsDashboard
                    v-else-if="
                      account?.feed === IApp.IFeedTypes.GoogleAnalytics && account.isSynced
                    "
                    :account="account"
                    :monthRange="{
                      start: _.head(months.selectedMonths)!,
                      end: _.last(months.selectedMonths)!
                    }"
                    :comparedMonthRange="{
                      start: _.head(months.comparedMonths)!,
                      end: _.last(months.comparedMonths)!
                    }"
                    :print-mode="mode"
                    :request-id="requestId"
                    :report-id="reportId"
                    :report="state.report"
                    :mode="mode"
                    :show-summary="showSummary"
                    @on-change-data="
                      upsertTotalData(account.account, 'Website Traffic Metrics', $event)
                    "
                    @on-change-data-load="
                      () => (loadingFinishedMap[IApp.IFeedTypes.GoogleAnalytics].value = true)
                    "
                  />
                </v-col>
              </v-row>
            </v-container>
          </Section>
        </template>
      </div>
    </v-container>
  </AuthGuard>
</template>

<script setup lang="ts">
import PageHeader from "@/components/PageHeader.vue";
import Profile from "@/actions/Profile";
import Report from "@/actions/Report";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import BirdEyeDashboard from "@/components/Dashboards/BirdEyeDashboard.vue";
import GoogleAnalyticsDashboard from "@/components/Dashboards/GoogleAnalyticsDashboard.vue";
import GoogleSearchConsoleDashboard from "@/components/Dashboards/GoogleSearchConsoleDashboard.vue";
import AISummaryEditor from "@/components/Widgets/AISummaryEditor.vue";
import Loading from "@/components/Widgets/Loading.vue";
import MonthRangePicker from "@/components/Widgets/MonthRangePicker.vue";
import Section from "@/components/Widgets/Section.vue";
import { Colors, URLs } from "@/constants";
import useRouteQuery from "@/hooks/useRouteQuery";
import { useAuthStore } from "@/stores/auth";
import { useCustomerStore } from "@/stores/customer";
import { useReportStore } from "@/stores/report";
import { IApp } from "@/types";
import {
  customerHasAnyDataSource,
  flatten,
  formatToShortMonth,
  getPastNMonths,
  isCustomerValid,
  isValid,
  iterateMonths,
  makeAlert
} from "@/utils";
import Auth from "@/utils/auth";
import _ from "lodash";
import moment from "moment";
import { computed, nextTick, onMounted, onUnmounted, reactive, type Ref, ref, watch } from "vue";
import { useRouter } from "vue-router";

const {
  customerIds,
  preview,
  dateRange,
  comparedRange,
  requestId,
  reportId,
  mode = IApp.PrintMode.View,
  showSummary = false,
  lazy
} = useRouteQuery();

const state = reactive({
  fetchAccountListLoading: false,
  isAccountListFetched: false,
  numberOfMonths: dateRange ? Array.from(iterateMonths(dateRange.start, dateRange.end)).length : 6,
  isPrintable: false,
  intervalId: null as number | null,
  customers: [] as IApp.ICustomer[],
  report: null as IApp.IReport | null,
  isReportLoading: false,
  isSummaryLoading: false,
  comparedScrollPosition: -1,
  totalData: [] as Array<{
    sourceId: string;
    title: string;
    data: Array<{ key: string; value: [] }>;
  }>,
  totalDataLoadStatus: [] as Array<{ sourceId: string; total: number; loaded: number }>,
  isSummarySaveLoading: false,
  isInitialSummaryGenerated: false,
  isGeneratePDFLoading: false,
  summary: "",
  selectedPromptId: null as string | null,
  isFetchingPrompt: false
});

const authStore = useAuthStore();
const reportStore = useReportStore();
const user = computed(() => authStore.user);

const router = useRouter();

const hasReportConfirmPermission = computed(() =>
  authStore.hasPermission(Auth.Permission.REPORT_CONFIRM)
);
const hasReportBuildPermission = computed(() =>
  authStore.hasPermission(Auth.Permission.REPORT_CREATE)
);
const reportBuildPermissionMessage = "You don't have permission to build report";
const generateReportTooltip = computed(() =>
  hasReportBuildPermission.value ? "Generate Report" : reportBuildPermissionMessage
);

const loadingFinishedMap: Record<IApp.IFeedTypes, Ref<boolean>> = {
  [IApp.IFeedTypes.GoogleSearchConsole]: ref(false),
  [IApp.IFeedTypes.GoogleAnalytics]: ref(false),
  [IApp.IFeedTypes.BirdEye]: ref(false)
};

const expectedFeeds = computed<IApp.IFeedTypes[]>(() => {
  const feeds = new Set<IApp.IFeedTypes>();
  state.customers.forEach((cust) =>
    filterAccounts(cust.accounts || [])
      .filter((a) => !!a.isSynced)
      .forEach((a) => feeds.add(a.feed))
  );
  return Array.from(feeds);
});

const feedDisplayName: Record<IApp.IFeedTypes, string> = {
  [IApp.IFeedTypes.GoogleSearchConsole]: "Google Search Console",
  [IApp.IFeedTypes.GoogleAnalytics]: "Google Analytics",
  [IApp.IFeedTypes.BirdEye]: "BirdEye"
};

const isLoadingFinished = computed(() =>
  expectedFeeds.value.every((feed) => loadingFinishedMap[feed].value)
);

const loadingNames = computed(() =>
  expectedFeeds.value
    .filter((feed) => !loadingFinishedMap[feed].value)
    .map((feed) => feedDisplayName[feed])
);

const onSummarySave = async (value: string, promptId: string | null) => {
  state.summary = value;
  state.selectedPromptId = promptId;
  if (requestId && reportId) {
    state.isSummarySaveLoading = true;
    const response = await Report.updateSummary(
      requestId,
      reportId,
      value,
      promptId === "default" ? null : promptId
    );
    if (response.status) {
      makeAlert("Summary saved successfully", "success");
    } else {
      makeAlert(response.message, "error");
    }
    state.isSummarySaveLoading = false;
  }
};

const onSummaryRegenerate = async (value: string): Promise<string | false> => {
  if (state.isSummarySaveLoading) return false;
  state.isSummarySaveLoading = true;
  const response = await Report.generateSummary(value);
  if (response.status) {
    state.isSummarySaveLoading = false;
    return response.data.summary;
  } else {
    state.isSummarySaveLoading = false;
    makeAlert(response.message, "error");
    return false;
  }
};

const upsertTotalData = (
  sourceId: string,
  title: string,
  data: Array<{ key: string; value: [] }>
) => {
  const index = _.findIndex(state.totalData, (item) => item.sourceId === sourceId);
  if (index > -1) {
    state.totalData[index] = {
      sourceId,
      title,
      data
    };
  } else {
    state.totalData = [
      ...state.totalData,
      {
        sourceId,
        title,
        data
      }
    ];
  }
};

const tempMonths = reactive<{
  selectedMonths: string[];
  comparedMonths: string[];
}>({
  selectedMonths: dateRange ? Array.from(iterateMonths(dateRange.start, dateRange.end)) : [],
  comparedMonths: comparedRange
    ? Array.from(iterateMonths(comparedRange.start, comparedRange.end))
    : []
});

const months = reactive<{
  selectedMonths: string[];
  comparedMonths: string[];
}>({
  selectedMonths: dateRange ? Array.from(iterateMonths(dateRange.start, dateRange.end)) : [],
  comparedMonths: comparedRange
    ? Array.from(iterateMonths(comparedRange.start, comparedRange.end))
    : []
});

const hasMonthRangeChange = computed(() => {
  return (
    !_.isEqual(tempMonths.selectedMonths, months.selectedMonths) ||
    !_.isEqual(tempMonths.comparedMonths, months.comparedMonths)
  );
});

const isMonthsSizeEqual = computed(() => {
  return (
    tempMonths.selectedMonths.length === tempMonths.comparedMonths.length &&
    tempMonths.selectedMonths.length === state.numberOfMonths
  );
});

const everyCustomerHasAtLeastOneSource = computed(() => {
  if (state.customers.length === 0) return false;
  return state.customers.every((c) => customerHasAnyDataSource(c));
});

const resetLoadingFlags = () => {
  expectedFeeds.value.forEach((feed) => {
    loadingFinishedMap[feed].value = false;
  });
};

const handleClickApply = async () => {
  if (!_.isEmpty(_.intersection(tempMonths.selectedMonths, tempMonths.comparedMonths))) {
    makeAlert("Selected months and compared months cannot overlap", "error");
    return;
  }
  state.summary = "";

  resetLoadingFlags();

  state.isInitialSummaryGenerated = false;
  months.selectedMonths = tempMonths.selectedMonths;
  months.comparedMonths = tempMonths.comparedMonths;

  await nextTick();
};

const customerStore = useCustomerStore();

const filterAccounts = (accounts: IApp.IAccount[]) => {
  return _(accounts)
    .filter(
      (item) =>
        item.status !== IApp.IAccountStatus.Paused && item.status !== IApp.IAccountStatus.Archived
    )
    .filter((item) =>
      isValid(
        item as
          | IApp.IGoogleSearchConsoleAccount
          | IApp.IGoogleAnalyticsAccount
          | IApp.IBirdEyeAccount
      )
    )
    .sortBy((item) => (item.feed === IApp.IFeedTypes.BirdEye ? 0 : 1))
    .value();
};

onMounted(async () => {
  if (!_.isEmpty(customerIds) && user.value?.id) {
    state.fetchAccountListLoading = true;
    state.customers = _(customerIds.value)
      .map((id) => customerStore.getCustomer(id))
      .filter((item): item is IApp.ICustomer => !!item && isCustomerValid(item))
      .value();
    state.fetchAccountListLoading = false;
    state.isAccountListFetched = true;
  }
});

onMounted(() => {
  if (dateRange && comparedRange) {
    tempMonths.selectedMonths = Array.from(iterateMonths(dateRange.start, dateRange.end));
    tempMonths.comparedMonths = Array.from(iterateMonths(comparedRange.start, comparedRange.end));
  } else if (state.report?.dateRange && state.report?.comparedRange) {
    tempMonths.selectedMonths = Array.from(
      iterateMonths(state.report.dateRange.start, state.report.dateRange.end)
    );
    tempMonths.comparedMonths = Array.from(
      iterateMonths(state.report.comparedRange.start, state.report.comparedRange.end)
    );
  } else {
    tempMonths.selectedMonths = getPastNMonths(state.numberOfMonths, moment().toDate());
    tempMonths.comparedMonths = getPastNMonths(
      state.numberOfMonths,
      moment().subtract(state.numberOfMonths, "months").toDate()
    );
  }

  months.selectedMonths = [...tempMonths.selectedMonths];
  months.comparedMonths = [...tempMonths.comparedMonths];
});

const isPresetChange = ref(false);

watch(
  () => state.numberOfMonths,
  (n) => {
    isPresetChange.value = true;

    const lastSelected =
      tempMonths.selectedMonths.length > 0
        ? tempMonths.selectedMonths[tempMonths.selectedMonths.length - 1]
        : moment().subtract(1, "month").format("YYYY-MM");

    const topStart = moment(lastSelected, "YYYY-MM")
      .subtract(n - 1, "months")
      .format("YYYY-MM");
    tempMonths.selectedMonths = Array.from(iterateMonths(topStart, lastSelected));

    const compEnd = moment(topStart, "YYYY-MM").subtract(1, "months").format("YYYY-MM");
    const compStart = moment(compEnd, "YYYY-MM")
      .subtract(n - 1, "months")
      .format("YYYY-MM");
    tempMonths.comparedMonths = Array.from(iterateMonths(compStart, compEnd));

    nextTick(() => {
      isPresetChange.value = false;
    });
  },
  { immediate: true }
);

watch(
  () => tempMonths.selectedMonths.join("|"),
  () => {
    if (isPresetChange.value) return;
    const n = state.numberOfMonths;
    const sel = tempMonths.selectedMonths;
    if (sel.length === n) {
      const start = moment(sel[0], "YYYY-MM").subtract(n, "months").format("YYYY-MM");
      const end = moment(start, "YYYY-MM")
        .add(n - 1, "months")
        .format("YYYY-MM");
      tempMonths.comparedMonths = Array.from(iterateMonths(start, end));
    }
  }
);

watch(months, (value) => {
  if (
    _.isEmpty(customerIds.value) ||
    _.isEmpty(value.selectedMonths) ||
    _.isEmpty(value.comparedMonths)
  ) {
    return;
  }
  const query = {
    "customer-id": customerIds.value,
    preview,
    "date-range": { start: _.head(value.selectedMonths), end: _.last(value.selectedMonths) },
    "compared-range": {
      start: _.head(value.comparedMonths),
      end: _.last(value.comparedMonths)
    },
    "request-id": requestId,
    "report-id": reportId,
    "show-summary": showSummary,
    mode,
    lazy
  };
  router.push({
    path: URLs.PAGE_MULTIPLE_CUSTOMER_DASHBOARD,
    query: flatten(query)
  });
});

const getReport = async () => {
  if (!reportId || !requestId) {
    return;
  }
  state.isReportLoading = true;
  state.report = await reportStore.getReport(requestId, reportId);
  state.summary = state.report?.summary ?? "";
  state.isReportLoading = false;
};

onMounted(() => {
  if (preview) {
    handleClickPrint();
  }
  getReport();
});

watch(isLoadingFinished, async (newValue) => {
  if (
    newValue &&
    !state.isInitialSummaryGenerated &&
    _.isEmpty(state.summary) &&
    showSummary &&
    requestId &&
    reportId &&
    !state.report?.summaryCreatedAt
  ) {
    state.isInitialSummaryGenerated = true;
    state.isFetchingPrompt = true;

    const prompt =
      state.report?.promptId === null || state.report?.promptId === "default"
        ? reportStore.getPrompt()
        : ((await Profile.getPrompt(state.report?.promptId as string)).data?.prompt.content ??
          reportStore.getPrompt());

    state.isFetchingPrompt = false;

    const summary = await onSummaryRegenerate(`${prompt} ${JSON.stringify(state.totalData)}`);

    if (summary) {
      state.summary = summary;
      await onSummarySave(summary, state.report?.promptId as string);
    }
  }
});

const handleClickPrint = () => {
  const originalTitle = document.title;

  document.body.classList.add("view");
  document.body.setAttribute("data-loading", "true");
  state.intervalId = setInterval(() => {
    const nLoading = document.querySelectorAll("[data-loading=true]").length;
    const nLoaded = document.querySelectorAll("[data-loading=false]").length;
    document.body.setAttribute("data-loading-loading", nLoading.toString());
    document.body.setAttribute("data-loading-loaded", nLoaded.toString());
    if (nLoading > 1 || nLoaded < 1) {
      return;
    }
    const charts = document.querySelectorAll(".vue-apexcharts");
    for (let i = 0; i < charts.length; i++) {
      const chart = charts.item(i);
      const canvas = chart.querySelector(".apexcharts-canvas");
      if (canvas) {
        if (Math.abs(chart.clientWidth - canvas.clientWidth) / chart.clientWidth < 0.1) {
          continue;
        }
        window.scrollTo({
          behavior: "smooth",
          top: chart.getBoundingClientRect().top + window.scrollY - 100
        });
        return;
      } else {
        return;
      }
    }

    if (state.intervalId) {
      clearInterval(state.intervalId);
      state.intervalId = null;
    }
    const newTitle = `${formatToShortMonth(_.head(months.selectedMonths)!)} - ${formatToShortMonth(_.last(months.selectedMonths)!)} Digital Visibility Report`;
    document.title = newTitle;

    document.body.setAttribute("data-loading", "false");
    if (!preview) {
      window.print();
      document.title = originalTitle;
      document.body.classList.remove("view");
    }
  }, 500);
};

const handleClickClose = () => {
  window.close();
};

const generateReport = async () => {
  if (!hasReportBuildPermission.value) {
    return;
  }
  if (!everyCustomerHasAtLeastOneSource.value) {
    makeAlert(
      "Every customer in this report must have at least one data source before you can generate.",
      "error"
    );
    return;
  }
  state.isGeneratePDFLoading = true;
  state.isSummarySaveLoading = true;
  const response = await Report.create({
    customerIds: customerIds.value,
    comparedRange: {
      start: _.head(months.comparedMonths)!,
      end: _.last(months.comparedMonths)!
    },
    dateRange: {
      start: _.head(months.selectedMonths)!,
      end: _.last(months.selectedMonths)!
    },
    showSummary: _.isEmpty(state.summary) ? false : true,
    summary: !_.isEmpty(state.summary) ? state.summary : undefined,
    promptId: state.selectedPromptId === "default" ? null : state.selectedPromptId
  });
  if (response.status) {
    makeAlert("Report generated successfully", "success");
    const report = response.data;
    if (report) {
      router.push(URLs.PAGE_REPORT_LIST);
    }
  } else {
    makeAlert(response.message, "error");
  }
  state.isGeneratePDFLoading = false;
  state.isSummarySaveLoading = false;
};

onMounted(() => {
  document.body.classList.add("print");
});

onUnmounted(() => {
  document.body.classList.remove("print");
});
</script>

<style scoped>
.selected {
  background-color: rgba(0, 123, 255, 0.5) !important;
  color: white !important;
  border-radius: 5px;
}

.btn-print {
  position: absolute;
  right: 0;
  top: 0;
}
</style>
